import { useQuery, type QueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import { useAuth } from '@clerk/clerk-react';
import type { SimulationCueSheetResponse } from '@/types/player/simulation-cue-sheet.types';

export const GET_SIMULATION_CUE_SHEET_QUERY_KEY = 'get-simulation-cue-sheet';

/** Mark stale so Control remount (Assets → Simulation) fetches even within staleTime. */
export function invalidateSimulationCueSheet(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: [GET_SIMULATION_CUE_SHEET_QUERY_KEY] });
}

export const useGetSimulationCueSheetQuery = (planFlowId: string | undefined, options?: { enabled?: boolean }) => {
  const { getToken } = useAuth();
  const enabled = !!planFlowId && (options?.enabled ?? true);

  return useQuery({
    queryKey: [GET_SIMULATION_CUE_SHEET_QUERY_KEY, planFlowId],
    queryFn: async () => {
      const token = await getToken();
      return getSimulationCueSheet({ token: token ?? '', planFlowId: planFlowId! });
    },
    enabled,
    // Single hydrate in flow-control-tab; avoid remount/refetch storms on preview trees.
    staleTime: 5_000,
    // Invalidated sheets refetch on remount; fresh entries remain reusable within staleTime.
    refetchOnMount: true,
  });
};

const getSimulationCueSheet = async ({
  token,
  planFlowId,
}: {
  token: string;
  planFlowId: string;
}): Promise<SimulationCueSheetResponse> => {
  const url = `${import.meta.env.VITE_API_URL}/player/simulation-cue-sheet?planFlowId=${encodeURIComponent(planFlowId)}`;
  return sendJSONRequest({ url, method: METHOD.GET, token });
};
